import { useState, useEffect, useMemo } from "react";
import { useUser } from "@/hooks/use-user";
import { useLocation } from "wouter";
import Layout from "@/components/layout";
import useSWR from "swr";
import { fetchCandidates } from "@/lib/api";
import type { Candidate } from "db/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CandidateForm from "@/components/candidate-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, MapPin, LinkedinIcon, Briefcase } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { debounce } from "lodash";

function CandidateCard({
  candidate,
  onClick,
}: {
  candidate: Candidate;
  onClick: () => void;
}) {
  return (
    <Card onClick={onClick} className="cursor-pointer">
      <CardContent className="pt-6">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{candidate.name}</h3>
            <Badge
              variant={candidate.status === "new" ? "default" : "secondary"}
            >
              {candidate.status}
            </Badge>
          </div>
          <div className="space-y-1 text-sm text-muted-foreground">
            {candidate.location && (
              <div className="flex items-center">
                <MapPin className="mr-2 h-4 w-4" />
                {candidate.location}
              </div>
            )}
            {candidate.experience && (
              <div className="flex items-center">
                <Briefcase className="mr-2 h-4 w-4" />
                {candidate.experience} years
              </div>
            )}
            {candidate.linkedInUrl && (
              <div className="flex items-center">
                <LinkedinIcon className="mr-2 h-4 w-4" />
                <a
                  href={candidate.linkedInUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  LinkedIn Profile
                </a>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Candidates() {
  const { user, isLoading: userLoading } = useUser();
  const [, setLocation] = useLocation();
  const { data: candidates, error, isLoading } = useSWR('candidates', fetchCandidates);
  const [open, setOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [filters, setFilters] = useState({
    name: "",
    email: "",
    location: "",
    experience: "",
    status: "",
    skills: ""
  });
  const [filtersVisible, setFiltersVisible] = useState(false);

  useEffect(() => {
    if (!userLoading && !user) {
      setLocation("/login");
    }
  }, [user, userLoading, setLocation]);

  const handleFilterChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, 300);

  const filteredCandidates = useMemo(
    () =>
      candidates?.filter((candidate) => {
        const isAdded = candidate.status === "added"; // Example condition
        const candidateLocation = candidate.location || "";
        const filterLocation = filters.location || "";
        const candidateName = candidate.name?.toLowerCase() || "";
        const candidateEmail = candidate.email?.toLowerCase() || "";
        const candidateStatus = candidate.status?.toLowerCase() || "";
        const candidateSkills = (candidate.skills?.length === 0) ? [] : candidate.skills;

        return (
          !isAdded &&
          (filters.name === "" || candidateName.includes(filters.name.toLowerCase())) &&
          (filters.email === "" || candidateEmail.includes(filters.email.toLowerCase())) &&
          (filterLocation === "" || candidateLocation.toLowerCase().includes(filterLocation.toLowerCase())) &&
          (filters.experience === "" || candidate.experience >= parseInt(filters.experience, 10)) &&
          (filters.status === "" || candidateStatus.includes(filters.status.toLowerCase())) &&
          (filters.skills === "" || (candidateSkills ? candidateSkills.map((skill: string) => skill.toLowerCase()).some((skill: string) => skill.includes(filters.skills.toLowerCase())): false))

        );
      }) || [],
    [candidates, filters]
  );

  const handleAddCandidateClick = () => {
    setSelectedCandidate(null);
    setOpen(true);
  };

  const handleCardClick = (candidate: Candidate) => {
    setLocation(`/candidate-details/${candidate.id}`);
  };

  const handleSuccess = () => {
    setOpen(false);
    setSelectedCandidate(null);
    mutate("/api/candidates");
  };

  const internalCandidates =
    filteredCandidates?.filter((c) => c.source === "internal") || [];
  const linkedInCandidates =
    filteredCandidates?.filter((c) => c.source === "linkedin") || [];

  // Add error handling
  if (error) {
    console.error('Error loading candidates:', error);
    return <div>Error loading candidates</div>;
  }

  // Show loading state
  if (isLoading || userLoading) {
    return <Layout><div>Loading...</div></Layout>;
  }

  if (!user) return null;

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Candidates</h2>
            <p className="text-muted-foreground">Manage your candidate pool</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleAddCandidateClick}>
                <Plus className="mr-2 h-4 w-4" />
                Add Candidate
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {selectedCandidate ? "Edit Candidate" : "Add New Candidate"}
                </DialogTitle>
              </DialogHeader>
              <CandidateForm
                onSuccess={handleSuccess}
                initialData={selectedCandidate}
              />
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div>
          <button
            onClick={() => setFiltersVisible(!filtersVisible)}
            className="mb-4 px-4 py-2 bg-black text-white rounded"
          >
            {filtersVisible ? "Hide Filters" : "Show Filters"}
          </button>

          {filtersVisible && (
            <div className="space-y-4 p-4 border rounded bg-gray-100">
              <div className="flex flex-wrap gap-4">
                {["name", "email", "location", "experience", "status", "skills"].map((filter) => (
                  <div className="flex flex-col" key={filter}>
                    <label className="block text-sm font-medium">{filter}</label>
                    <input
                      type="text"
                      name={filter}
                      onChange={handleFilterChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <Tabs defaultValue="internal">
          <TabsList>
            <TabsTrigger value="internal">Internal Candidates</TabsTrigger>
            <TabsTrigger value="linkedin">LinkedIn Candidates</TabsTrigger>
          </TabsList>
          <TabsContent value="internal" className="mt-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {internalCandidates.map((candidate) => (
                <CandidateCard
                  key={candidate.id}
                  candidate={candidate}
                  onClick={() => handleCardClick(candidate)}
                />
              ))}
            </div>
          </TabsContent>
          <TabsContent value="linkedin" className="mt-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {linkedInCandidates.map((candidate) => (
                <CandidateCard
                  key={candidate.id}
                  candidate={candidate}
                  onClick={() => handleCardClick(candidate)}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
